import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Avatar, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Divider, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  CardActions, 
  Button, 
  TextField,
  Chip,
  Tabs,
  Tab,
  CircularProgress,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  Badge,
  useTheme,
  Menu,
  MenuItem,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Select,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Breadcrumbs
} from '@mui/material';
import {
  Person,
  CarRental,
  Settings,
  Payment,
  Delete,
  Refresh,
  CheckCircle,
  Star,
  StarBorder,
  Edit,
  Business,
  AddPhotoAlternate,
  Visibility,
  VisibilityOff,
  Close,
  HelpOutline,
  CreditCard,
  LocalOffer,
  DirectionsCar,
  CalendarToday,
  Speed,
  LocalGasStation,
  AttachFile,
  FileDownload,
  Calculate,
  Inventory,
  InsertChart,
  MoreVert,
  Verified,
  Sell,
  WatchLater,
  Check,
  Clear,
  Warning,
  ExpandMore,
  Folder,
  FolderOpen,
  PersonAdd,
  Group,
  BusinessCenter,
  NoteAdd,
  CreateNewFolder,
  InsertDriveFile,
  Notifications,
  PushPin,
  Info,
  CameraAlt
} from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';
import { getAuth } from 'firebase/auth';
import { doc, getDoc, updateDoc, collection, query, where, getDocs, deleteDoc, setDoc, addDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase-config';
import { useNavigate } from 'react-router-dom';
import { uploadBytes, getStorage, ref as storageRef, getDownloadURL } from 'firebase/storage';

const DashboardContainer = styled(Container)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: '100%',
  [theme.breakpoints.up('lg')]: {
    maxWidth: '1920px'
  }
}));

const Sidebar = styled(Paper)(({ theme }) => ({
  width: 280,
  padding: theme.spacing(3),
  borderRadius: 12,
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[3],
  position: 'sticky',
  top: theme.spacing(2),
  border: 'none'
}));

const ProfileCard = styled(Card)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(3),
  borderRadius: 12,
  boxShadow: 'none',
  border: `1px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(3),
  position: 'relative'
}));

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
  width: 100,
  height: 100,
  margin: '0 auto',
  marginBottom: theme.spacing(2),
  fontSize: '2.5rem',
  border: `3px solid ${theme.palette.primary.main}`,
  boxShadow: theme.shadows[2]
}));

const NavItem = styled(ListItem)(({ theme, selected }) => ({
  borderRadius: 8,
  marginBottom: theme.spacing(0.5),
  backgroundColor: selected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.05)
  },
  '& .MuiListItemIcon-root': {
    minWidth: 36,
    color: selected ? theme.palette.primary.main : theme.palette.text.secondary
  },
  '& .MuiListItemText-primary': {
    fontWeight: selected ? 600 : 400,
    color: selected ? theme.palette.primary.main : theme.palette.text.primary
  }
}));

const MainContent = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: 12,
  boxShadow: theme.shadows[2],
  minHeight: 'calc(100vh - 32px)',
  backgroundColor: theme.palette.background.paper,
  position: 'relative'
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(4),
  position: 'relative'
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.5rem',
  color: theme.palette.text.primary,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1)
}));

const VehicleCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 12,
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  boxShadow: theme.shadows[1],
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4]
  }
}));

const VehicleImage = styled('div', {
  shouldForwardProp: (prop) => prop !== 'src'
})(({ src }) => ({
  height: 180,
  backgroundImage: `url("${src}")`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  position: 'relative'
}));

const StatusBadge = styled(Chip)(({ theme, status }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  right: theme.spacing(2),
  fontWeight: 600,
  backgroundColor: 
    status === 'sold' ? theme.palette.error.main : 
    status === 'reserved' ? theme.palette.warning.main : 
    theme.palette.success.main,
  color: theme.palette.common.white
}));

const PremiumBadge = styled(Chip)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  left: theme.spacing(2),
  fontWeight: 600,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white
}));

const UrgentBadge = styled(Chip)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  left: theme.spacing(2),
  fontWeight: 600,
  backgroundColor: theme.palette.error.main,
  color: theme.palette.common.white
}));

const RentBadge = styled(Chip)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  left: theme.spacing(2),
  fontWeight: 600,
  backgroundColor: theme.palette.info.main,
  color: theme.palette.common.white
}));

const PlanCard = styled(Card)(({ theme, active }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 12,
  border: active ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[4]
  }
}));

const PlanHeader = styled(CardContent)(({ theme, active }) => ({
  backgroundColor: active ? alpha(theme.palette.primary.main, 0.08) : theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  textAlign: 'center',
  padding: theme.spacing(4, 2),
  '&:last-child': {
    paddingBottom: theme.spacing(4)
  }
}));

const PlanPrice = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  color: theme.palette.primary.main,
  margin: theme.spacing(2, 0),
  fontSize: '2rem'
}));

const PlanFeature = styled(ListItem)(({ theme }) => ({
  paddingLeft: 0,
  paddingRight: 0,
  '& .MuiListItemIcon-root': {
    minWidth: 32,
    color: theme.palette.primary.main
  }
}));

const StatsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 12,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  backgroundColor: theme.palette.background.default
}));

const StatsValue = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '2rem',
  margin: theme.spacing(1, 0)
}));

const StatsLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  display: 'flex',
  alignItems: 'center'
}));

const ClientStatusChip = styled(Chip)(({ theme, status }) => ({
  fontWeight: 600,
  backgroundColor: 
    status === 'new_lead' ? alpha(theme.palette.info.main, 0.2) :
    status === 'interested' ? alpha(theme.palette.success.main, 0.2) :
    status === 'not_qualified' ? alpha(theme.palette.error.main, 0.2) :
    alpha(theme.palette.warning.main, 0.2),
  color: 
    status === 'new_lead' ? theme.palette.info.dark :
    status === 'interested' ? theme.palette.success.dark :
    status === 'not_qualified' ? theme.palette.error.dark :
    theme.palette.warning.dark,
  '& .MuiChip-icon': {
    color: 
      status === 'new_lead' ? theme.palette.info.main :
      status === 'interested' ? theme.palette.success.main :
      status === 'not_qualified' ? theme.palette.error.main :
      theme.palette.warning.main
  }
}));

const NotificationBadge = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: -5,
  right: -5,
  backgroundColor: theme.palette.error.main,
  color: theme.palette.common.white,
  borderRadius: '50%',
  width: 20,
  height: 20,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 12,
  fontWeight: 'bold'
}));

const RequiredFieldIndicator = styled('span')(({ theme }) => ({
  color: theme.palette.error.main,
  marginLeft: theme.spacing(0.5)
}));

const AutoSalonPage = () => {
  const [documents, setDocuments] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [userData, setUserData] = useState(null);
  const [userAds, setUserAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    dealerName: '',
    address: '',
    city: '',
    subscription: 'free',
    avatar: '',
    description: '',
    premiumCredits: 0,
    whatsapp: '',
    viber: ''
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [adToDelete, setAdToDelete] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [avatarPreview, setAvatarPreview] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedAd, setSelectedAd] = useState(null);
  const [coverPhotoIndex, setCoverPhotoIndex] = useState(0);
  const [inventoryData, setInventoryData] = useState({
    brand: '',
    model: '',
    year: '',
    mileage: '',
    purchasePrice: '',
    sellingPrice: ''
  });
  const [clients, setClients] = useState([]);
  const [newClient, setNewClient] = useState({
    name: '',
    email: '',
    phone: '',
    status: 'new_lead',
    interestedVehicle: '',
    notes: ''
  });
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [showLogoWarning, setShowLogoWarning] = useState(false);
  
  const auth = getAuth();
  const navigate = useNavigate();
  const storage = getStorage();

  const [inventoryList, setInventoryList] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({
    totalAds: 0,
    activeAds: 0,
    soldAds: 0,
    reservedAds: 0,
    totalProfit: 0
  });

  const [inventoryStats, setInventoryStats] = useState({
    totalVehicles: 0,
    avgProfit: 0,
    avgDaysToSell: 0
  });
  
  const [financeData, setFinanceData] = useState({
    carPrice: 0,
    downPayment: 0,
    interestRate: 5,
    loanTerm: 60,
    monthlyPayment: 0
  });

  const calculateMonthlyPayment = () => {
    const principal = financeData.carPrice - financeData.downPayment;
    const monthlyRate = financeData.interestRate / 100 / 12;
    const payment = principal * monthlyRate * Math.pow(1 + monthlyRate, financeData.loanTerm) / 
                   (Math.pow(1 + monthlyRate, financeData.loanTerm) - 1);
    return payment.toFixed(2);
  };

  useEffect(() => {
    if (userData && !userData.avatar) {
      setShowLogoWarning(true);
    }
  }, [userData]);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;

        const q = query(collection(db, "clients"), where("userId", "==", user.uid));
        const unsubscribe = onSnapshot(q, (snapshot) => {
          const clientsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setClients(clientsList);
        });

        return () => unsubscribe();
      } catch (error) {
        console.error("Error fetching clients:", error);
      }
    };

    fetchClients();
  }, [auth.currentUser]);

  useEffect(() => {
    const fetchFolders = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;

        const q = query(collection(db, "folders"), where("userId", "==", user.uid));
        const snapshot = await getDocs(q);
        const foldersList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setFolders(foldersList);
      } catch (error) {
        console.error("Error fetching folders:", error);
      }
    };

    fetchFolders();
  }, [auth.currentUser]);

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;
  
        let q;
        if (currentFolder) {
          q = query(collection(db, "documents"), 
            where("userId", "==", user.uid),
            where("folderId", "==", currentFolder.id));
        } else {
          q = query(collection(db, "documents"), 
            where("userId", "==", user.uid),
            where("folderId", "==", null));
        }
        
        const snapshot = await getDocs(q);
        const docsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setDocuments(docsList);
      } catch (error) {
        console.error("Error fetching documents:", error);
      }
    };
  
    fetchDocuments();
  }, [auth.currentUser, currentFolder]);

  useEffect(() => {
    const fetchInventory = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;

        const q = query(collection(db, "inventory"), where("userId", "==", user.uid));
        const unsubscribe = onSnapshot(q, (snapshot) => {
          const vehicles = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setInventoryList(vehicles);

          const totalVehicles = vehicles.length;
          const totalProfit = vehicles.reduce((sum, v) => {
            const profit = (v.sellingPrice || 0) - (v.purchasePrice || 0);
            return sum + profit;
          }, 0);
          const avgProfit = totalVehicles > 0 ? totalProfit / totalVehicles : 0;

          setInventoryStats({
            totalVehicles,
            avgProfit: avgProfit.toFixed(2),
            avgDaysToSell: 0
          });
        });

        return () => unsubscribe();
      } catch (error) {
        console.error("Error fetching inventory:", error);
      }
    };

    fetchInventory();
  }, [auth.currentUser]);

  useEffect(() => {
    setFinanceData(prev => ({
      ...prev,
      monthlyPayment: calculateMonthlyPayment()
    }));
  }, [financeData.carPrice, financeData.downPayment, financeData.interestRate, financeData.loanTerm]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClientInputChange = (e) => {
    const { name, value } = e.target;
    setNewClient(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleInventoryChange = (e) => {
    const { name, value } = e.target;
    setInventoryData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveClient = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      if (selectedClient) {
        await updateDoc(doc(db, "clients", selectedClient.id), newClient);
        showSnackbar('Клиентот е успешно ажуриран');
      } else {
        await addDoc(collection(db, "clients"), {
          userId: user.uid,
          ...newClient,
          createdAt: new Date().toISOString()
        });
        showSnackbar('Клиентот е успешно додаден');
      }

      setClientDialogOpen(false);
      setNewClient({
        name: '',
        email: '',
        phone: '',
        status: 'new_lead',
        interestedVehicle: '',
        notes: ''
      });
      setSelectedClient(null);
    } catch (error) {
      console.error("Error saving client:", error);
      showSnackbar('Грешка при зачувување на клиент', 'error');
    }
  };

  const handleDeleteClient = async (id) => {
    try {
      await deleteDoc(doc(db, "clients", id));
      showSnackbar('Клиентот е успешно избришан');
    } catch (error) {
      console.error("Error deleting client:", error);
      showSnackbar('Грешка при бришење на клиент', 'error');
    }
  };

  const handleEditClient = (client) => {
    setSelectedClient(client);
    setNewClient({
      name: client.name,
      email: client.email,
      phone: client.phone,
      status: client.status,
      interestedVehicle: client.interestedVehicle,
      notes: client.notes
    });
    setClientDialogOpen(true);
  };

  const handleCreateFolder = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;
  
      await addDoc(collection(db, "folders"), {
        userId: user.uid,
        name: newFolderName,
        createdAt: new Date().toISOString()
      });
  
      showSnackbar('Фолдерот е успешно креиран');
      setNewFolderName('');
      setFolderDialogOpen(false);
  
      const refreshFolders = async () => {
        const q = query(collection(db, "folders"), where("userId", "==", user.uid));
        const snapshot = await getDocs(q);
        const foldersList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setFolders(foldersList);
      };
  
      await refreshFolders();
  
    } catch (error) {
      console.error("Error creating folder:", error);
      showSnackbar('Грешка при креирање на фолдер', 'error');
    }
  };
  

  const handleDeleteFolder = async (id) => {
    try {
      const q = query(collection(db, "documents"), 
        where("userId", "==", auth.currentUser.uid),
        where("folderId", "==", id));
      
      const snapshot = await getDocs(q);
      const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);

      await deleteDoc(doc(db, "folders", id));
      
      showSnackbar('Фолдерот и неговите документи се успешно избришани');
      if (currentFolder && currentFolder.id === id) {
        setCurrentFolder(null);
      }
    } catch (error) {
      console.error("Error deleting folder:", error);
      showSnackbar('Грешка при бришење на фолдер', 'error');
    }
  };

  const handleSaveInventory = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;
  
      const inventoryItem = {
        userId: user.uid,
        ...inventoryData,
        createdAt: new Date().toISOString(),
        purchasePrice: Number(inventoryData.purchasePrice),
        sellingPrice: Number(inventoryData.sellingPrice),
      };
  
      await addDoc(collection(db, "inventory"), inventoryItem);
  
      showSnackbar('Возилото е успешно зачувано во инвентар');
  
      setInventoryData({
        brand: '',
        model: '',
        year: '',
        mileage: '',
        purchasePrice: '',
        sellingPrice: ''
      });
  
    } catch (error) {
      console.error("Error saving inventory:", error);
      showSnackbar('Грешка при зачувување на возило', 'error');
    }
  };

  const handleDeleteInventory = async (id) => {
    try {
      await deleteDoc(doc(db, "inventory", id));
      showSnackbar('Возилото е успешно избришано');
    } catch (error) {
      console.error("Error deleting inventory:", error);
      showSnackbar('Грешка при бришење на возило', 'error');
    }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
      setShowLogoWarning(false);
    }
  };

  const handleSave = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      let avatarUrl = formData.avatar;
      if (avatarFile) {
        const fileRef = storageRef(storage, `avatars/${user.uid}`);
        await uploadBytes(fileRef, avatarFile);
        avatarUrl = await getDownloadURL(fileRef);
      }

      await updateDoc(doc(db, "users", user.uid), {
        ...formData,
        avatar: avatarUrl
      });

      setUserData(prev => ({
        ...prev,
        ...formData,
        avatar: avatarUrl
      }));

      setEditMode(false);
      showSnackbar('Податоците се успешно зачувани');
      setShowLogoWarning(false);
    } catch (error) {
      console.error("Error saving profile:", error);
      showSnackbar('Грешка при зачувување на податоци', 'error');
    }
  };

  const handleCancel = () => {
    setFormData({
      name: userData?.name || '',
      email: userData?.email || '',
      phone: userData?.phone || '',
      dealerName: userData?.dealerName || '',
      address: userData?.address || '',
      city: userData?.city || '',
      subscription: userData?.subscription || 'free',
      avatar: userData?.avatar || '',
      description: userData?.description || '',
      premiumCredits: userData?.premiumCredits || 0,
      whatsapp: userData?.whatsapp || '',
      viber: userData?.viber || ''
    });
    setAvatarPreview(userData?.avatar || '');
    setAvatarFile(null);
    setEditMode(false);
    setShowLogoWarning(!userData?.avatar);
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const user = auth.currentUser;
        if (!user) {
          navigate('/login');
          return;
        }

        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          
          if (data.userType !== 'dealer' && data.role !== 'dealer') {
            navigate('/user');
            return;
          }
          
          setUserData(data);
          setFormData({
            name: data.name || '',
            email: data.email || '',
            phone: data.phone || '',
            dealerName: data.dealerName || '',
            address: data.address || '',
            city: data.city || '',
            subscription: data.subscription || 'free',
            avatar: data.avatar || '',
            description: data.description || '',
            premiumCredits: data.premiumCredits || 0,
            whatsapp: data.whatsapp || '',
            viber: data.viber || ''
          });
          
          if (data.avatar) {
            setAvatarPreview(data.avatar);
            setShowLogoWarning(false);
          } else {
            setShowLogoWarning(true);
          }
        }

        const adsQuery = query(collection(db, "ads"), where("userId", "==", user.uid));
        const adsSnapshot = await getDocs(adsQuery);
        const ads = adsSnapshot.docs.map(doc => {
          const data = doc.data();
          const isRent = data.adType === 'rent';
        
          let displayPrice = 'Цена не е внесена';
          if (isRent && data.dailyPrice !== undefined && data.dailyPrice !== null) {
            displayPrice = `${data.dailyPrice} €/ден`;
          } else if (!isRent && data.price !== undefined && data.price !== null) {
            displayPrice = `${data.price} €`;
          }
        
          return {
            id: doc.id,
            ...data,
            images: data.images || [],
            premium: data.premium || false,
            status: data.status || 'active',
            isUrgent: data.isUrgent || false,
            coverPhotoIndex: typeof data.coverPhotoIndex === 'number' ? data.coverPhotoIndex : 0,
            isRent,
            price: displayPrice
          };
        });
        
        setUserAds(ads);
        
        const totalAds = ads.length;
        const activeAds = ads.filter(ad => ad.status === 'active').length;
        const soldAds = ads.filter(ad => ad.status === 'sold').length;
        const reservedAds = ads.filter(ad => ad.status === 'reserved').length;

        const totalProfit = ads
          .filter(ad => ad.status === 'sold')
          .reduce((sum, ad) => {
            const sellingPrice = Number(ad.sellingPrice) || Number(ad.price) || 0;
            const purchasePrice = Number(ad.purchasePrice) || 0;
            return sum + (sellingPrice - purchasePrice);
          }, 0);

        setDashboardStats({
          totalAds,
          activeAds,
          soldAds,
          reservedAds,
          totalProfit
        });

      } catch (error) {
        console.error("Error fetching user data:", error);
        showSnackbar('Грешка при вчитување на податоци', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [auth.currentUser, navigate]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleMenuOpen = (event, ad) => {
    setAnchorEl(event.currentTarget);
    setSelectedAd(ad);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedAd(null);
  };

  const handleAdAction = (action) => {
    if (!selectedAd) return;
    
    switch(action) {
      case 'premium':
        handleAdPremiumChange(selectedAd.id, selectedAd.premium);
        break;
      case 'sold':
        handleAdStatusChange(selectedAd.id, 'sold');
        break;
      case 'reserved':
        handleAdStatusChange(selectedAd.id, 'reserved');
        break;
      case 'active':
        handleAdStatusChange(selectedAd.id, 'active');
        break;
      case 'edit':
        handleEditAd(selectedAd);
        break;
      case 'delete':
        handleDeleteAd(selectedAd.id);
        break;
      case 'urgent':
        handleAdUrgentChange(selectedAd.id, selectedAd.isUrgent);
        break;
      case 'verify':
        break;
      default:
        break;
    }
    
    handleMenuClose();
  };

  const handleAdStatusChange = async (id, status) => {
    try {
      await updateDoc(doc(db, "ads", id), { status });
      setUserAds(prev => prev.map(ad => ad.id === id ? { ...ad, status } : ad));
      showSnackbar(`Огласот е успешно ${status === 'sold' ? 'означен како продаден' : 
                   status === 'reserved' ? 'означен како резервиран' : 'активиран'}`);
    } catch (error) {
      console.error("Error updating ad status:", error);
      showSnackbar('Грешка при ажурирање на статус', 'error');
    }
  };

  const handleAdPremiumChange = async (id, isPremium) => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      if (!isPremium && userData.premiumCredits <= 0) {
        showSnackbar('Немате преостанати кредити за премиум огласи', 'error');
        return;
      }

      await updateDoc(doc(db, "ads", id), { premium: !isPremium });

      const creditsChange = isPremium ? 1 : -1;
      const newCredits = userData.premiumCredits + creditsChange;
      
      await updateDoc(doc(db, "users", user.uid), { premiumCredits: newCredits });
      
      setUserAds(prev => prev.map(ad => ad.id === id ? { ...ad, premium: !isPremium } : ad));
      setUserData(prev => ({ ...prev, premiumCredits: newCredits }));
      
      showSnackbar(`Огласот е успешно ${!isPremium ? 'направен премиум' : 'вратен во обичен'}`);
    } catch (error) {
      console.error("Error updating ad premium status:", error);
      showSnackbar('Грешка при ажурирање на премиум статус', 'error');
    }
  };

  const handleAdUrgentChange = async (id, isUrgent) => {
    try {
      await updateDoc(doc(db, "ads", id), { 
        isUrgent: !isUrgent,
        urgentAt: !isUrgent ? new Date().toISOString() : null
      });
      setUserAds(prev => prev.map(ad => ad.id === id ? { ...ad, isUrgent: !isUrgent } : ad));
      showSnackbar(`Огласот е успешно ${!isUrgent ? 'означен како итен' : 'вратен во нормален'}`);
    } catch (error) {
      console.error("Error updating ad urgent status:", error);
      showSnackbar('Грешка при ажурирање на итен статус', 'error');
    }
  };

  const handleDeleteAd = (id) => {
    setAdToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteAd = async () => {
    try {
      await deleteDoc(doc(db, "ads", adToDelete));
      setUserAds(prev => prev.filter(ad => ad.id !== adToDelete));
      showSnackbar('Огласот е успешно избришан');
    } catch (error) {
      console.error("Error deleting ad:", error);
      showSnackbar('Грешка при бришење на оглас', 'error');
    } finally {
      setDeleteDialogOpen(false);
      setAdToDelete(null);
    }
  };

  const handleEditAd = (ad) => {
    setSelectedAd(ad);
    setCoverPhotoIndex(ad.coverPhotoIndex || 0);
    navigate(`/edit-ad/${ad.id}`);
  };

  const handleCoverPhotoChange = (index) => {
    setCoverPhotoIndex(index);
  };

  const handleSubscriptionSelect = (plan) => {
    showSnackbar(`Претплатата ${plan} е успешно избрана`);
  };

  const subscriptionPlans = [
    {
      type: 'free',
      title: 'Бесплатен',
      subtitle: 'Добар за почеток',
      price: '0',
      period: '/годишно',
      features: [
        '10 огласи',
        'Основен dashboard',
        'Ограничени функции'
      ],
      buttonText: 'Тековен план'
    },
    {
      type: 'basic',
      title: 'Стандарден',
      subtitle: 'Идеален за мали салони',
      price: '500',
      period: '/годишно',
      features: [
        '100 огласи',
        '2 премиум огласи',
        '1 итен оглас',
        'Напреден dashboard'
      ],
      buttonText: 'Избери'
    },
    {
      type: 'premium',
      title: 'Премиум',
      subtitle: 'Најдобар избор за професионалци',
      price: '1,000',
      period: '/годишнно',
      features: [
        'Неограничено објавување',
        '10 премиум огласи',
        '5 итен огласи',
        'Напреден dashboard',
        'Прилагодена URL адреса за својот салон (avtoklik.com/вашиотсалон)',
        'Верифициран Знак за доверба'
      ],
      buttonText: 'Избери'
    }
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!userData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <Typography variant="h6">Не е пронајден корисник</Typography>
      </Box>
    );
  }

  return (
    <DashboardContainer>
      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <Sidebar>
            <ProfileCard>
              {editMode ? (
                <Box sx={{ position: 'relative', margin: '0 auto', width: 100, height: 100 }}>
                  <ProfileAvatar 
                    alt={formData.dealerName || formData.name} 
                    src={avatarPreview || '/default-avatar.png'}
                  />
                  <label htmlFor="avatar-upload">
                    <input
                      id="avatar-upload"
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleAvatarChange}
                    />
                    <IconButton
                      component="span"
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                        '&:hover': {
                          backgroundColor: theme.palette.primary.dark,
                        }
                      }}
                    >
                      <CameraAlt fontSize="small" />
                    </IconButton>
                  </label>
                </Box>
              ) : (
                <Box sx={{ position: 'relative' }}>
                  <ProfileAvatar 
                    alt={userData?.dealerName || userData?.name} 
                    src={userData?.avatar || '/default-avatar.png'}
                  />
                  {showLogoWarning && (
                    <Tooltip title="Додадете лого за подобар изглед на вашиот профил" arrow>
                      <NotificationBadge>
                        <Info fontSize="small" />
                      </NotificationBadge>
                    </Tooltip>
                  )}
                </Box>
              )}
              
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {userData?.dealerName || userData?.name}
              </Typography>
              
              <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', gap: 1 }}>
                <Chip 
                  label="Автосалон" 
                  color="primary" 
                  size="small" 
                  icon={<Business fontSize="small" />}
                  sx={{ fontWeight: 500 }}
                />
                <Chip 
                  label={userData?.subscription === 'free' ? 'Бесплатен' : 
                         userData?.subscription === 'basic' ? 'Стандарден' : 'Премиум'} 
                  color={userData?.subscription === 'free' ? 'default' : 'primary'} 
                  size="small"
                  sx={{ fontWeight: 500 }}
                />
              </Box>
            </ProfileCard>

            <List component="nav">
              <NavItem 
                button 
                selected={activeTab === 'dashboard'}
                onClick={() => setActiveTab('dashboard')}
              >
                <ListItemIcon><InsertChart /></ListItemIcon>
                <ListItemText primary="Dashboard" />
              </NavItem>
              
              <NavItem 
                button 
                selected={activeTab === 'ads'}
                onClick={() => setActiveTab('ads')}
              >
                <ListItemIcon><CarRental /></ListItemIcon>
                <ListItemText primary="Огласи" />
                <Badge badgeContent={userAds.length} color="primary" />
              </NavItem>
              
              <NavItem 
                button 
                selected={activeTab === 'clients'}
                onClick={() => setActiveTab('clients')}
              >
                <ListItemIcon><Group /></ListItemIcon>
                <ListItemText primary="Клиенти" />
                <Badge badgeContent={clients.length} color="primary" />
              </NavItem>
              
              <NavItem 
                button 
                selected={activeTab === 'inventory'}
                onClick={() => setActiveTab('inventory')}
              >
                <ListItemIcon><Inventory /></ListItemIcon>
                <ListItemText primary="Инвентар" />
              </NavItem>
              
              <NavItem 
                button 
                selected={activeTab === 'finance'}
                onClick={() => setActiveTab('finance')}
              >
                <ListItemIcon><Calculate /></ListItemIcon>
                <ListItemText primary="Финансиски калкулатор" />
              </NavItem>
              
              <NavItem 
                button 
                selected={activeTab === 'documents'}
                onClick={() => setActiveTab('documents')}
              >
                <ListItemIcon><AttachFile /></ListItemIcon>
                <ListItemText primary="Документи" />
              </NavItem>
              
              <NavItem 
                button 
                selected={activeTab === 'profile'}
                onClick={() => setActiveTab('profile')}
              >
                <ListItemIcon><Person /></ListItemIcon>
                <ListItemText primary="Податоци за салон" />
                {showLogoWarning && (
                  <Box sx={{ ml: 1 }}>
                    <Notifications color="error" fontSize="small" />
                  </Box>
                )}
              </NavItem>
              
              <NavItem 
                button 
                selected={activeTab === 'subscription'}
                onClick={() => setActiveTab('subscription')}
              >
                <ListItemIcon><Payment /></ListItemIcon>
                <ListItemText primary="Претплата" />
              </NavItem>
              
              <NavItem 
                button 
                selected={activeTab === 'settings'}
                onClick={() => setActiveTab('settings')}
              >
                <ListItemIcon><Settings /></ListItemIcon>
                <ListItemText primary="Подесувања" />
              </NavItem>
            </List>

            <Box sx={{ mt: 2 }}>
              <Button 
                variant="contained" 
                color="primary" 
                fullWidth
                component={Link}
                to="/post-ad"
                startIcon={<CarRental />}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600
                }}
              >
                Нов Оглас
              </Button>
            </Box>
          </Sidebar>
        </Grid>

        <Grid item xs={12} md={9}>
          <MainContent>
            {activeTab === 'dashboard' && (
              <Box>
                <SectionHeader>
                  <SectionTitle>Dashboard</SectionTitle>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button 
                      variant="outlined" 
                      startIcon={<Refresh />}
                      onClick={() => window.location.reload()}
                      sx={{ borderRadius: 2 }}
                    >
                      Освежи
                    </Button>
                  </Box>
                </SectionHeader>

                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatsCard>
                      <StatsLabel>
                        <DirectionsCar sx={{ mr: 1 }} /> Вкупно огласи
                      </StatsLabel>
                      <StatsValue>{dashboardStats.totalAds}</StatsValue>
                      <LinearProgress variant="determinate" value={(dashboardStats.activeAds / dashboardStats.totalAds) * 100} />
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {dashboardStats.activeAds} активни
                      </Typography>
                    </StatsCard>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatsCard>
                      <StatsLabel>
                        <Sell sx={{ mr: 1 }} /> Продадени
                      </StatsLabel>
                      <StatsValue color="success">{dashboardStats.soldAds}</StatsValue>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {Math.round((dashboardStats.soldAds / dashboardStats.totalAds) * 100)}% успешност
                      </Typography>
                    </StatsCard>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatsCard>
                      <StatsLabel>
                        <WatchLater sx={{ mr: 1 }} /> Резервирани
                      </StatsLabel>
                      <StatsValue color="warning">{dashboardStats.reservedAds}</StatsValue>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {dashboardStats.reservedAds} активни резервации
                      </Typography>
                    </StatsCard>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatsCard>
                      <StatsLabel>
                        <CreditCard sx={{ mr: 1 }} /> Вкупен профит
                      </StatsLabel>
                      <StatsValue color="primary">{dashboardStats.totalProfit.toLocaleString()} €</StatsValue>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Просечен профит: {dashboardStats.soldAds > 0 ? Math.round(dashboardStats.totalProfit / dashboardStats.soldAds).toLocaleString() : 0} €
                      </Typography>
                    </StatsCard>
                  </Grid>
                </Grid>

                <SectionHeader>
                  <SectionTitle>Неодамнешни огласи</SectionTitle>
                  <Button 
                    variant="text" 
                    color="primary"
                    onClick={() => setActiveTab('ads')}
                  >
                    Види ги сите
                  </Button>
                </SectionHeader>

                {userAds.length === 0 ? (
                  <Box sx={{ 
                    textAlign: 'center', 
                    py: 8,
                    border: `2px dashed ${theme.palette.divider}`,
                    borderRadius: 2
                  }}>
                    <CarRental sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h5" color="textSecondary" gutterBottom>
                      Немате објавено огласи
                    </Typography>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      component={Link}
                      to="/post-ad"
                      size="large"
                      sx={{ mt: 2 }}
                    >
                      Објави нов оглас
                    </Button>
                  </Box>
                ) : (
                  <Grid container spacing={3}>
                    {userAds.slice(0, 4).map((ad) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={ad.id}>
                        <VehicleCard>
                        <VehicleImage
 src={
  Array.isArray(ad.images) && ad.images.length > 0
    ? ad.images[typeof ad.coverPhotoIndex === 'number' ? ad.coverPhotoIndex : 0]
    : 'https://source.unsplash.com/random/600x400/?car'
}
>
                            {ad.premium && <PremiumBadge label="Премиум" size="small" />}
                            {ad.isUrgent && <UrgentBadge label="ИТНО" size="small" sx={{ left: ad.premium ? 100 : 12 }} />}
                            {ad.isRent && <RentBadge label="ИЗНАЈМУВАЊЕ" size="small" sx={{ left: ad.premium ? 100 : 12 }} />}
                            {ad.status !== 'active' && (
                              <StatusBadge 
                                label={ad.status === 'sold' ? 'Продадено' : 'Резервирано'} 
                                status={ad.status}
                                size="small"
                              />
                            )}
                          </VehicleImage>
                          
                          <CardContent>
                            <Typography gutterBottom variant="h6" component="div" noWrap>
                              {ad.brand} {ad.model}
                            </Typography>
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="h6" color="primary" fontWeight={700}>
                                {ad.price}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {ad.year} год.
                              </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                {ad.mileage} км
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {ad.fuelType}
                              </Typography>
                            </Box>
                          </CardContent>
                          
                          <CardActions sx={{ justifyContent: 'flex-end' }}>
                            <IconButton
                              aria-label="more"
                              aria-controls="ad-menu"
                              aria-haspopup="true"
                              onClick={(e) => handleMenuOpen(e, ad)}
                            >
                              <MoreVert />
                            </IconButton>
                          </CardActions>
                        </VehicleCard>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            )}

            {activeTab === 'ads' && (
              <Box>
                <SectionHeader>
                  <SectionTitle>Огласи на салон</SectionTitle>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button 
                      variant="contained" 
                      color="primary"
                      component={Link}
                      to="/post-ad"
                      startIcon={<CarRental />}
                    >
                      Нов Оглас
                    </Button>
                  </Box>
                </SectionHeader>
                
                <Tabs
                  value={activeTab}
                  onChange={(e, newValue) => setActiveTab(newValue)}
                  sx={{ mb: 3 }}
                >
                  <Tab label="Сите огласи" value="all" />
                  <Tab label="Продажба" value="sell" />
                  <Tab label="Изнајмување" value="rent" />
                </Tabs>
                
                {userAds.length === 0 ? (
                  <Box sx={{ 
                    textAlign: 'center', 
                    py: 8,
                    border: `2px dashed ${theme.palette.divider}`,
                    borderRadius: 2
                  }}>
                    <CarRental sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h5" color="textSecondary" gutterBottom>
                      Немате објавено огласи
                    </Typography>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      component={Link}
                      to="/post-ad"
                      size="large"
                      sx={{ mt: 2 }}
                    >
                      Објави нов оглас
                    </Button>
                  </Box>
                ) : (
                  <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Оглас</TableCell>
                          <TableCell align="center">Тип</TableCell>
                          <TableCell align="center">Статус</TableCell>
                          <TableCell align="center">Цена</TableCell>
                          <TableCell align="center">Премиум</TableCell>
                          <TableCell align="center">ИТНО</TableCell>
                          <TableCell align="center">Верификувано</TableCell>
                          <TableCell align="right">Акции</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {userAds.map((ad) => (
                          <TableRow key={ad.id}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar 
                                  src={ad.images?.[ad.coverPhotoIndex || 0]} 
                                  variant="rounded"
                                  sx={{ width: 56, height: 56, mr: 2 }}
                                />
                                <Box>
                                  <Typography variant="subtitle1">
                                    {ad.brand} {ad.model}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {ad.year} • {ad.mileage} км
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Chip 
                                label={ad.isRent ? 'Изнајмување' : 'Продажба'}
                                color={ad.isRent ? 'info' : 'primary'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip 
                                label={ad.status === 'sold' ? 'Продадено' : 
                                      ad.status === 'reserved' ? 'Резервирано' : 'Активно'}
                                color={
                                  ad.status === 'sold' ? 'error' : 
                                  ad.status === 'reserved' ? 'warning' : 'success'
                                }
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Typography fontWeight={600}>
                                {ad.price}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              {ad.premium ? (
                                <Check color="primary" />
                              ) : (
                                <Clear color="error" />
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {ad.isUrgent ? (
                                <Check color="primary" />
                              ) : (
                                <Clear color="error" />
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {ad.verified ? (
                                <Verified color="success" />
                              ) : (
                                <Clear color="error" />
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                aria-label="more"
                                aria-controls="ad-menu"
                                aria-haspopup="true"
                                onClick={(e) => handleMenuOpen(e, ad)}
                              >
                                <MoreVert />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}

{activeTab === 'clients' && (
  <Box>
    <SectionHeader>
      <SectionTitle>Клиенти 🧑‍🤝‍🧑</SectionTitle>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button 
          variant="contained" 
          color="primary"
          startIcon={<PersonAdd />}
          onClick={() => setClientDialogOpen(true)}
        >
          Додади клиент
        </Button>
      </Box>
    </SectionHeader>

    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Статистика за клиенти 📊
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard>
                <StatsLabel>
                  <Group sx={{ mr: 1 }} /> Вкупно клиенти
                </StatsLabel>
                <StatsValue>{clients.length}</StatsValue>
              </StatsCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard>
                <StatsLabel>
                  <BusinessCenter sx={{ mr: 1 }} /> Потенцијални
                </StatsLabel>
                <StatsValue color="info">
                  {clients.filter(c => c.status === 'new_lead').length}
                </StatsValue>
              </StatsCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard>
                <StatsLabel>
                  <CheckCircle sx={{ mr: 1 }} /> Заинтересирани
                </StatsLabel>
                <StatsValue color="success">
                  {clients.filter(c => c.status === 'interested').length}
                </StatsValue>
              </StatsCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard>
                <StatsLabel>
                  <Warning sx={{ mr: 1 }} /> Не квалификувани
                </StatsLabel>
                <StatsValue color="error">
                  {clients.filter(c => c.status === 'not_qualified').length}
                </StatsValue>
              </StatsCard>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Листа на клиенти 📋
          </Typography>
          {clients.length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              py: 6,
              border: `2px dashed ${theme.palette.divider}`,
              borderRadius: 2
            }}>
              <Group sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                Немате додадено клиенти
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Кликнете на "Додади клиент" за да започнете
              </Typography>
              <Button 
                variant="contained" 
                color="primary"
                startIcon={<PersonAdd />}
                onClick={() => setClientDialogOpen(true)}
              >
                Додади клиент
              </Button>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Име</TableCell>
                    <TableCell>Контакт</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell>Заинтересиран за</TableCell>
                    <TableCell align="right">Акции</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clients.map((client) => (
                    <TableRow key={client.id} hover>
                      <TableCell>
                        <Typography fontWeight={600}>{client.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{client.phone}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {client.email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <ClientStatusChip
                          label={
                            client.status === 'new_lead' ? 'Нов лид ✨' :
                            client.status === 'interested' ? 'Заинтересиран 👍' :
                            client.status === 'not_qualified' ? 'Не квалификуван 👎' : 'Неодреден'
                          }
                          status={client.status}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {client.interestedVehicle ? (
                          <Chip 
                            label={client.interestedVehicle}
                            color="primary"
                            size="small"
                            icon={<DirectionsCar fontSize="small" />}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Нема податоци
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                          <Tooltip title="Уреди">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleEditClient(client)}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Избриши">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleDeleteClient(client.id)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Grid>
    </Grid>
  </Box>
)}

            {activeTab === 'inventory' && (
              <Box>
                <SectionHeader>
                  <SectionTitle>Инвентар на возила</SectionTitle>
                  <Button 
                    variant="contained" 
                    color="primary"
                    startIcon={<CarRental />}
                    onClick={handleSaveInventory}
                  >
                    Додади ново возило
                  </Button>
                </SectionHeader>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        Додади ново возило
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField 
                            fullWidth 
                            label="Бренд" 
                            name="brand"
                            value={inventoryData.brand}
                            onChange={handleInventoryChange}
                            variant="outlined"
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField 
                            fullWidth 
                            label="Модел" 
                            name="model"
                            value={inventoryData.model}
                            onChange={handleInventoryChange}
                            variant="outlined"
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField 
                            fullWidth 
                            label="Година" 
                            name="year"
                            value={inventoryData.year}
                            onChange={handleInventoryChange}
                            variant="outlined"
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField 
                            fullWidth 
                            label="Километража" 
                            name="mileage"
                            value={inventoryData.mileage}
                            onChange={handleInventoryChange}
                            variant="outlined"
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField 
                            fullWidth 
                            label="Купена цена (€)" 
                            name="purchasePrice"
                            value={inventoryData.purchasePrice}
                            onChange={handleInventoryChange}
                            variant="outlined"
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField 
                            fullWidth 
                            label="Продажна цена (€)" 
                            name="sellingPrice"
                            value={inventoryData.sellingPrice}
                            onChange={handleInventoryChange}
                            variant="outlined"
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Button 
                            variant="contained" 
                            color="primary" 
                            fullWidth
                            onClick={handleSaveInventory}
                            sx={{ mt: 1 }}
                          >
                            Зачувај возило
                          </Button>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        Инвентарска статистика
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={4}>
                          <StatsCard>
                            <StatsLabel>
                              <DirectionsCar sx={{ mr: 1 }} /> Вкупно возила
                            </StatsLabel>
                            <StatsValue>{inventoryStats.totalVehicles}</StatsValue>
                          </StatsCard>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <StatsCard>
                            <StatsLabel>
                              <CreditCard sx={{ mr: 1 }} /> Просечен профит
                            </StatsLabel>
                            <StatsValue color="primary">{inventoryStats.avgProfit} €</StatsValue>
                          </StatsCard>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <StatsCard>
                            <StatsLabel>
                              <CalendarToday sx={{ mr: 1 }} /> Просечно време
                            </StatsLabel>
                            <StatsValue>{inventoryStats.avgDaysToSell} дена</StatsValue>
                          </StatsCard>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  <Grid item xs={12}>
                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Листа на возила
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Вкупно: {inventoryList.length} возила
                        </Typography>
                      </Box>

                      {inventoryList.length === 0 ? (
                        <Box sx={{ 
                          textAlign: 'center', 
                          py: 6,
                          border: `2px dashed ${theme.palette.divider}`,
                          borderRadius: 2
                        }}>
                          <DirectionsCar sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                          <Typography variant="h6" color="textSecondary" gutterBottom>
                            Немате внесени возила во инвентар
                          </Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                            Додадете ново возило за да започнете
                          </Typography>
                        </Box>
                      ) : (
                        <TableContainer>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>Бренд</TableCell>
                                <TableCell>Модел</TableCell>
                                <TableCell align="right">Година</TableCell>
                                <TableCell align="right">Километража</TableCell>
                                <TableCell align="right">Купена цена</TableCell>
                                <TableCell align="right">Продажна цена</TableCell>
                                <TableCell align="right">Профит</TableCell>
                                <TableCell align="center">Акции</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {inventoryList.map((item) => (
                                <TableRow key={item.id} hover>
                                  <TableCell>{item.brand}</TableCell>
                                  <TableCell>{item.model}</TableCell>
                                  <TableCell align="right">{item.year}</TableCell>
                                  <TableCell align="right">{item.mileage} км</TableCell>
                                  <TableCell align="right">{item.purchasePrice} €</TableCell>
                                  <TableCell align="right">{item.sellingPrice} €</TableCell>
                                  <TableCell align="right" sx={{ 
                                    fontWeight: 600,
                                    color: (item.sellingPrice - item.purchasePrice) >= 0 ? 
                                      theme.palette.success.main : theme.palette.error.main
                                  }}>
                                    {(item.sellingPrice - item.purchasePrice)} €
                                  </TableCell>
                                  <TableCell align="center">
                                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                                      <Tooltip title="Уреди">
                                        <IconButton 
                                          size="small" 
                                          color="primary"
                                          onClick={() => {
                                            setInventoryData({
                                              brand: item.brand,
                                              model: item.model,
                                              year: item.year,
                                              mileage: item.mileage,
                                              purchasePrice: item.purchasePrice,
                                              sellingPrice: item.sellingPrice
                                            });
                                            showSnackbar('Податоците за возилото се внесени. Можете да ги уредите горе и повторно да кликнете на Зачувај возило', 'info');
                                          }}
                                        >
                                          <Edit fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Резервирај">
                                        <IconButton 
                                          size="small" 
                                          color="warning"
                                          onClick={() => {
                                            showSnackbar('Возилото е резервирано', 'success');
                                          }}
                                        >
                                          <WatchLater fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Избриши">
                                        <IconButton 
                                          size="small" 
                                          color="error"
                                          onClick={() => handleDeleteInventory(item.id)}
                                        >
                                          <Delete fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}

            {activeTab === 'finance' && (
              <Box>
                <SectionHeader>
                  <SectionTitle>Финансиски калкулатор</SectionTitle>
                </SectionHeader>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        Калкулатор за финансирање
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Цена на возило"
                            value={financeData.carPrice}
                            onChange={(e) => setFinanceData({...financeData, carPrice: e.target.value})}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">€</InputAdornment>,
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Првична уплата"
                            value={financeData.downPayment}
                            onChange={(e) => setFinanceData({...financeData, downPayment: e.target.value})}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">€</InputAdornment>,
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Каматна стапка"
                            value={financeData.interestRate}
                            onChange={(e) => setFinanceData({...financeData, interestRate: e.target.value})}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormControl fullWidth>
                            <InputLabel>Период на отплата</InputLabel>
                            <Select
                              value={financeData.loanTerm}
                              label="Период на отплата"
                              onChange={(e) => setFinanceData({...financeData, loanTerm: e.target.value})}
                            >
                              <MenuItem value={12}>12 месеци</MenuItem>
                              <MenuItem value={24}>24 месеци</MenuItem>
                              <MenuItem value={36}>36 месеци</MenuItem>
                              <MenuItem value={48}>48 месеци</MenuItem>
                              <MenuItem value={60}>60 месеци</MenuItem>
                              <MenuItem value={92}>92 месеци</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        Резултати
                      </Typography>
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          Месечна рата
                        </Typography>
                        <Typography variant="h3" fontWeight={700} color="primary">
                          {financeData.monthlyPayment} €
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          Вкупен износ на кредит
                        </Typography>
                        <Typography variant="h4" fontWeight={700}>
                          {financeData.carPrice - financeData.downPayment} €
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Вкупен износ на камата
                        </Typography>
                        <Typography variant="h4" fontWeight={700}>
                          {((financeData.monthlyPayment * financeData.loanTerm) - (financeData.carPrice - financeData.downPayment)).toFixed(2)} €
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}

            {activeTab === 'documents' && (
              <Box>
                <SectionHeader>
                  <SectionTitle>Документи 📂</SectionTitle>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button 
                      variant="contained" 
                      color="primary"
                      startIcon={<CreateNewFolder />}
                      onClick={() => setFolderDialogOpen(true)}
                    >
                      Нов фолдер
                    </Button>
                    <Button 
                      variant="outlined" 
                      color="primary"
                      startIcon={<AttachFile />}
                      component="label"
                    >
                      Прикачи документ
                      <input
                        type="file"
                        hidden
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (file) {
                            try {
                              const user = auth.currentUser;
                              if (!user) return;

                              const fileRef = storageRef(storage, `documents/${user.uid}/${file.name}`);
                              await uploadBytes(fileRef, file);
                              const downloadURL = await getDownloadURL(fileRef);

                              const docRef = await addDoc(collection(db, "documents"), {
                                userId: user.uid,
                                name: file.name,
                                url: downloadURL,
                                type: file.type,
                                size: file.size,
                                folderId: currentFolder?.id || null,
                                uploadedAt: new Date().toISOString()
                              });
                              
                              setDocuments(prev => [
                                ...prev,
                                {
                                  id: docRef.id,
                                  userId: user.uid,
                                  name: file.name,
                                  url: downloadURL,
                                  type: file.type,
                                  size: file.size,
                                  folderId: currentFolder?.id || null,
                                  uploadedAt: new Date().toISOString()
                                }
                              ]);
                              
                              showSnackbar('Документот е успешно прикачен');
                              
                            } catch (error) {
                              console.error("Error uploading document:", error);
                              showSnackbar('Грешка при прикачување на документ', 'error');
                            }
                          }
                        }}
                      />
                    </Button>
                  </Box>
                </SectionHeader>

                {currentFolder && (
                  <Box sx={{ mb: 3 }}>
                    <Breadcrumbs aria-label="breadcrumb">
                      <Button 
                        color="inherit" 
                        startIcon={<FolderOpen />}
                        onClick={() => setCurrentFolder(null)}
                      >
                        Сите документи
                      </Button>
                      <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
                        <Folder sx={{ mr: 1 }} />
                        {currentFolder.name}
                      </Typography>
                    </Breadcrumbs>
                  </Box>
                )}

                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, borderRadius: 2 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                        <Folder sx={{ mr: 1 }} /> Фолдери
                      </Typography>
                      <List>
                        {folders.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                            Немате креирано фолдери
                          </Typography>
                        ) : (
                          folders.map((folder) => (
                            <ListItem 
                              key={folder.id} 
                              button 
                              selected={currentFolder?.id === folder.id}
                              onClick={() => setCurrentFolder(folder)}
                              sx={{
                                borderRadius: 1,
                                '&.Mui-selected': {
                                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.15),
                                  }
                                }
                              }}
                            >
                              <ListItemIcon>
                                {currentFolder?.id === folder.id ? <FolderOpen color="primary" /> : <Folder />}
                              </ListItemIcon>
                              <ListItemText primary={folder.name} />
                              <IconButton
                                edge="end"
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteFolder(folder.id);
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </ListItem>
                          ))
                        )}
                      </List>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        {currentFolder ? `Документи во "${currentFolder.name}"` : 'Сите документи'}
                      </Typography>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Име на документ</TableCell>
                              <TableCell>Тип</TableCell>
                              <TableCell>Големина</TableCell>
                              <TableCell>Датум на прикачување</TableCell>
                              <TableCell align="right">Акции</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {documents.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={5} align="center">
                                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                                    {currentFolder ? 
                                      'Нема документи во овој фолдер' : 
                                      'Нема прикачени документи'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              documents.map((documentItem) => (
                                <TableRow key={documentItem.id} hover>
                                  <TableCell sx={{ display: 'flex', alignItems: 'center' }}>
                                    <InsertDriveFile sx={{ mr: 1, color: 'text.secondary' }} />
                                    {documentItem.name}
                                  </TableCell>
                                  <TableCell>{documentItem.type}</TableCell>
                                  <TableCell>{(documentItem.size / 1024).toFixed(1)} KB</TableCell>
                                  <TableCell>{new Date(documentItem.uploadedAt).toLocaleString()}</TableCell>
                                  <TableCell align="right">
                                    <Button
                                      href={documentItem.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      startIcon={<FileDownload />}
                                      size="small"
                                      sx={{ mr: 1 }}
                                    >
                                      Превземи
                                    </Button>
                                    <Button
                                      color="error"
                                      size="small"
                                      startIcon={<Delete />}
                                      onClick={async () => {
                                        try {
                                          await deleteDoc(doc(db, "documents", documentItem.id));
                                          setDocuments(prev => prev.filter(d => d.id !== documentItem.id));
                                          showSnackbar('Документот е избришан');
                                        } catch (error) {
                                          console.error("Error deleting document:", error);
                                          showSnackbar('Грешка при бришење на документ', 'error');
                                        }
                                      }}
                                    >
                                      Избриши
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))    
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}

            {activeTab === 'profile' && (
              <Box>
                <SectionHeader>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SectionTitle>Податоци за салон</SectionTitle>
                    {showLogoWarning && (
                      <Tooltip title="Додадете лого за подобар изглед на вашиот профил" arrow>
                        <Notifications color="error" />
                      </Tooltip>
                    )}
                  </Box>
                  {editMode ? (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button 
                        variant="contained" 
                        onClick={handleSave}
                        sx={{ borderRadius: 2 }}
                      >
                        Зачувај
                      </Button>
                      <Button 
                        variant="outlined" 
                        onClick={handleCancel}
                        sx={{ borderRadius: 2 }}
                      >
                        Откажи
                      </Button>
                    </Box>
                  ) : (
                    <Button 
                      variant="contained" 
                      startIcon={<Edit />} 
                      onClick={() => setEditMode(true)}
                      sx={{ borderRadius: 2 }}
                    >
                      Уреди
                    </Button>
                  )}
                </SectionHeader>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ position: 'relative' }}>
                      <TextField
                        fullWidth
                        label="Име на автосалон"
                        name="dealerName"
                        value={formData.dealerName}
                        onChange={handleInputChange}
                        disabled={!editMode}
                        variant="outlined"
                        sx={{ mb: 3 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <PushPin color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                      {!formData.dealerName && editMode && (
                        <Typography variant="caption" color="error" sx={{ position: 'absolute', bottom: 8, left: 0 }}>
                          Ова поле е задолжително
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ position: 'relative' }}>
                      <TextField
                        fullWidth
                        label="Контакт лице"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        disabled={!editMode}
                        variant="outlined"
                        sx={{ mb: 3 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <PushPin color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                      {!formData.name && editMode && (
                        <Typography variant="caption" color="error" sx={{ position: 'absolute', bottom: 8, left: 0 }}>
                          Ова поле е задолжително
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ position: 'relative' }}>
                      <TextField
                        fullWidth
                        label="Е-пошта"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        disabled={!editMode}
                        variant="outlined"
                        sx={{ mb: 3 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <PushPin color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                      {!formData.email && editMode && (
                        <Typography variant="caption" color="error" sx={{ position: 'absolute', bottom: 8, left: 0 }}>
                          Ова поле е задолжително
                        </Typography>
                      )}
                    </Box>
                    <TextField
                      fullWidth
                      label="Телефон"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      disabled={!editMode}
                      variant="outlined"
                      sx={{ mb: 3 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="WhatsApp број"
                      name="whatsapp"
                      value={formData.whatsapp}
                      onChange={handleInputChange}
                      disabled={!editMode}
                      variant="outlined"
                      sx={{ mb: 3 }}
                    />
                    <TextField
                      fullWidth
                      label="Viber број"
                      name="viber"
                      value={formData.viber}
                      onChange={handleInputChange}
                      disabled={!editMode}
                      variant="outlined"
                      sx={{ mb: 3 }}
                    />
                    <TextField
                      fullWidth
                      label="Адреса"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      disabled={!editMode}
                      variant="outlined"
                      sx={{ mb: 3 }}
                    />
                    <TextField
                      fullWidth
                      label="Град"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      disabled={!editMode}
                      variant="outlined"
                      sx={{ mb: 3 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Опис на салон"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      disabled={!editMode}
                      variant="outlined"
                      multiline
                      rows={4}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {activeTab === 'subscription' && (
              <Box>
                <SectionHeader>
                  <SectionTitle>Претплата</SectionTitle>
                </SectionHeader>

                <Paper sx={{ 
                  p: 3, 
                  borderRadius: 2, 
                  mb: 4, 
                  backgroundColor: alpha(theme.palette.primary.light, 0.05),
                  borderLeft: `4px solid ${theme.palette.primary.main}`
                }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Тековна претплата
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip 
                      label={userData.subscription === 'free' ? 'Бесплатна' : 
                            userData.subscription === 'basic' ? 'Стандардна' : 'Премиум'} 
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                    <Typography variant="body2" color="text.secondary">
                      
                    </Typography>
                  </Box>
                </Paper>

                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                  Достапни планови
                </Typography>

                <Grid container spacing={3}>
                  {subscriptionPlans.map((plan) => (
                    <Grid item xs={12} md={4} key={plan.type}>
                      <PlanCard active={userData.subscription === plan.type}>
                        <PlanHeader active={userData.subscription === plan.type}>
                          <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            {plan.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {plan.subtitle}
                          </Typography>
                          <PlanPrice>
                            {plan.price} MKD <span style={{ fontSize: '1rem', fontWeight: 400 }}>{plan.period}</span>
                          </PlanPrice>
                        </PlanHeader>
                        <CardContent>
                          <List disablePadding>
                            {plan.features.map((feature, index) => (
                              <PlanFeature key={index}>
                                <ListItemIcon><CheckCircle fontSize="small" /></ListItemIcon>
                                <ListItemText primary={feature} />
                              </PlanFeature>
                            ))}
                          </List>
                        </CardContent>
                        <CardActions sx={{ justifyContent: 'center', pb: 3 }}>
                          <Button
                            variant={userData.subscription === plan.type ? 'contained' : 'outlined'}
                            color="primary"
                            onClick={() => handleSubscriptionSelect(plan.type)}
                            disabled={userData.subscription === plan.type}
                            fullWidth
                            sx={{ 
                              mx: 2,
                              fontWeight: 600,
                              borderRadius: 1
                            }}
                          >
                            {userData.subscription === plan.type ? 'Тековен план' : 'Се надоградува при објава оглас'}
                          </Button>
                        </CardActions>
                      </PlanCard>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {activeTab === 'settings' && (
              <Box>
                <SectionHeader>
                  <SectionTitle>Подесувања</SectionTitle>
                </SectionHeader>

                <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Промена на лозинка
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Тековна лозинка"
                        type={showPassword ? 'text' : 'password'}
                        variant="outlined"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Нова лозинка"
                        type={showPassword ? 'text' : 'password'}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button 
                        variant="contained" 
                        color="primary"
                        sx={{ mt: 2 }}
                      >
                        Зачувај нова лозинка
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>

                <Paper sx={{ p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Напредни опции
                  </Typography>
                  <Button 
                    variant="outlined" 
                    color="error"
                    startIcon={<Delete />}
                  >
                    Деактивирај профил
                  </Button>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Внимание: Оваа акција е неповратна и ќе ги избрише сите ваши податоци.
                  </Typography>
                </Paper>
              </Box>
            )}
          </MainContent>
        </Grid>
      </Grid>

      {/* Client Dialog */}
      <Dialog
        open={clientDialogOpen}
        onClose={() => {
          setClientDialogOpen(false);
          setSelectedClient(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {selectedClient ? 'Уреди клиент' : 'Додади нов клиент'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Име и презиме *"
                name="name"
                value={newClient.name}
                onChange={handleClientInputChange}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Е-пошта"
                name="email"
                value={newClient.email}
                onChange={handleClientInputChange}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Телефон *"
                name="phone"
                value={newClient.phone}
                onChange={handleClientInputChange}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Статус</InputLabel>
                <Select
                  name="status"
                  value={newClient.status}
                  onChange={handleClientInputChange}
                  label="Статус"
                >
                  <MenuItem value="new_lead">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography>Нов лид</Typography>
                      <Typography sx={{ ml: 1, color: 'text.secondary' }}>✨</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="interested">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography>Заинтересиран</Typography>
                      <Typography sx={{ ml: 1, color: 'text.secondary' }}>👍</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="not_qualified">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography>Не квалификуван</Typography>
                      <Typography sx={{ ml: 1, color: 'text.secondary' }}>👎</Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Заинтересиран за возило"
                name="interestedVehicle"
                value={newClient.interestedVehicle}
                onChange={handleClientInputChange}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DirectionsCar />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Белешки"
                name="notes"
                value={newClient.notes}
                onChange={handleClientInputChange}
                variant="outlined"
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setClientDialogOpen(false);
              setSelectedClient(null);
            }}
          >
            Откажи
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleSaveClient}
          >
            Зачувај
          </Button>
        </DialogActions>
      </Dialog>

      {/* Folder Dialog */}
      <Dialog
        open={folderDialogOpen}
        onClose={() => setFolderDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Креирај нов фолдер</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Име на фолдер"
            type="text"
            fullWidth
            variant="outlined"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFolderDialogOpen(false)}>Откажи</Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleCreateFolder}
            disabled={!newFolderName.trim()}
          >
            Креирај
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        id="ad-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleAdAction('premium')} disabled={selectedAd?.status === 'sold'}>
          <ListItemIcon>
            {selectedAd?.premium ? <StarBorder /> : <Star />}
          </ListItemIcon>
          {selectedAd?.premium ? 'Одстрани премиум' : 'Направи премиум'}
        </MenuItem>
        <MenuItem onClick={() => handleAdAction('urgent')} disabled={selectedAd?.status === 'sold'}>
          <ListItemIcon>
            {selectedAd?.isUrgent ? <Warning color="error" /> : <Warning />}
          </ListItemIcon>
          {selectedAd?.isUrgent ? 'Одстрани ИТНО' : 'Означи како ИТНО'}
        </MenuItem>
        <MenuItem onClick={() => handleAdAction('verify')}>
          <ListItemIcon><Verified /></ListItemIcon>
          Означи како верификувано
        </MenuItem>
        <Divider />
        {selectedAd?.status === 'active' ? (
          [
            <MenuItem key="reserved" onClick={() => handleAdAction('reserved')}>
              <ListItemIcon><WatchLater /></ListItemIcon>
              Означи како резервирано
            </MenuItem>,
            <MenuItem key="sold" onClick={() => handleAdAction('sold')}>
              <ListItemIcon><Sell /></ListItemIcon>
              Означи како продадено
            </MenuItem>
          ]
        ) : (
          <MenuItem onClick={() => handleAdAction('active')}>
            <ListItemIcon><Check /></ListItemIcon>
            Врати во активни
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => handleAdAction('edit')}>
          <ListItemIcon><Edit /></ListItemIcon>
          Уреди оглас
        </MenuItem>
        <MenuItem onClick={() => handleAdAction('delete')}>
          <ListItemIcon><Delete color="error" /></ListItemIcon>
          <Typography color="error">Избриши оглас</Typography>
        </MenuItem>
      </Menu>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Потврди бришење</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Дали сте сигурни дека сакате да го избришете овој оглас? Оваа акција не може да се врати.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Откажи</Button>
          <Button onClick={confirmDeleteAd} color="error" variant="contained">
            Потврди бришење
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </DashboardContainer>
  );
};

export default AutoSalonPage;